import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../shared/ui/dialog';
import { Button } from '../../../../shared/ui/button';
import { dialogFooterClassName, cancelButtonClassName, primaryButtonClassName, labelClassName, fieldClassName } from '../../../../shared/ui/formStyles';
import { Input } from '../../../../shared/ui/input';
import { Label } from '../../../../shared/ui/label';
import { Loader2, Landmark } from 'lucide-react';
import PropTypes from 'prop-types';

// Backend expects Amount{1,2}Cents in 1..99. UI presents cents (matches what Stripe deposits
// and what users see on their bank statement: a few-cent deposit with descriptor "SM xxxxxx").
export function VerifyAchDialog({ isOpen, onClose, onVerify, methodToken, dataTestId }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [amounts, setAmounts] = useState({ amount1: '', amount2: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const a1 = Number.parseInt(amounts.amount1, 10);
      const a2 = Number.parseInt(amounts.amount2, 10);
      const payload = {
        token: methodToken,
        amount1Cents: a1,
        amount2Cents: a2,
      };
      const success = await onVerify(payload);
      if (success) {
        onClose();
        setAmounts({ amount1: '', amount2: '' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[400px]" data-testid={dataTestId}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" dataTestId={dataTestId ? `${dataTestId}-title` : undefined}>
            <Landmark size={20} className="text-brand-solid" />
            Verify Bank Account
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-fg-muted mb-6">
            Enter the two micro-deposit amounts (in cents, 1–99) that posted to your bank
            account. Stripe&apos;s descriptor starts with <code>SM</code>.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid={dataTestId ? `${dataTestId}-form` : undefined}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount1" className={labelClassName} dataTestId={dataTestId ? `${dataTestId}-amount1-label` : undefined}>Amount 1 (cents)</Label>
                <Input
                  id="amount1"
                  type="number"
                  min={1}
                  max={99}
                  placeholder="32"
                  value={amounts.amount1}
                  onChange={(e) => setAmounts({ ...amounts, amount1: e.target.value })}
                  className={fieldClassName}
                  required
                  dataTestId={dataTestId ? `${dataTestId}-amount1-input` : undefined}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount2" className={labelClassName} dataTestId={dataTestId ? `${dataTestId}-amount2-label` : undefined}>Amount 2 (cents)</Label>
                <Input
                  id="amount2"
                  type="number"
                  min={1}
                  max={99}
                  placeholder="45"
                  value={amounts.amount2}
                  onChange={(e) => setAmounts({ ...amounts, amount2: e.target.value })}
                  className={fieldClassName}
                  required
                  dataTestId={dataTestId ? `${dataTestId}-amount2-input` : undefined}
                />
              </div>
            </div>

            <DialogFooter className={`${dialogFooterClassName} !px-0`} dataTestId={dataTestId ? `${dataTestId}-footer` : undefined}>
              <Button type="button" variant="outline" className={cancelButtonClassName} onClick={onClose} disabled={isSubmitting} dataTestId={dataTestId ? `${dataTestId}-cancel-button` : undefined}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className={primaryButtonClassName}
                dataTestId={dataTestId ? `${dataTestId}-verify-button` : undefined}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Account'
                )}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

VerifyAchDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
  methodToken: PropTypes.string,
};
